import React, { type ReactNode, useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { useHandleWishlist } from '@pkg/components/Wishlist/hooks';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { wishlistActions } from '@app/store/slices';

import { Button, StarIcon, ToastLink, ToastText, WishlistToastInner, WishlistToastWrapper } from './styles';

interface Props {
    label?: string;
    moveUp?: boolean;
    hideStar?: boolean;
    children?: ReactNode;
}

const WishlistComponent = ({ hideStar, label, moveUp, children }: Props) => {
    return (
        <WishlistToastWrapper $moveUp={moveUp}>
            <WishlistToastInner $center={hideStar}>
                {hideStar ? null : <StarIcon emojiID="⭐" />}
                {label ? <ToastText>{label}</ToastText> : null}
                {children}
            </WishlistToastInner>
        </WishlistToastWrapper>
    );
};

const WishlistToastAdded = ({ label }: Props) => {
    const dispatch = useAppDispatch();
    const wishListToastSlug = useAppSelector((state) => state.wishlist.wishListToastSlug);
    const wishListToastName = useAppSelector((state) => state.wishlist.wishListToastName);

    const handleShowWishlistModal = () => {
        dispatch(wishlistActions.setWishListModalSlug(wishListToastSlug));
        dispatch(wishlistActions.setWishListToastSlug(null));
        dispatch(wishlistActions.setWishListToastName(null));
    };

    useEffect(() => {
        let timeoutId;
        if (wishListToastSlug) {
            timeoutId = setTimeout(() => {
                dispatch(wishlistActions.setWishListToastSlug(null));
            }, 5000);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [dispatch, wishListToastSlug]);

    return (
        <WishlistComponent label={wishListToastName ? undefined : label}>
            {wishListToastName ? (
                <ToastLink to={`/wishlists/${wishListToastSlug}`}>Added to {wishListToastName}</ToastLink>
            ) : null}
            <Button onClick={handleShowWishlistModal}>Manage</Button>
        </WishlistComponent>
    );
};

const WishlistToastRemove = ({ hideStar }: Props) => {
    const dispatch = useAppDispatch();
    const { wishListToastSlug, removeWishListToastSlug, removeWishListToastName, undoWishlistData } = useAppSelector(
        (state) => state.wishlist,
    );

    const { handleAddFlow } = useHandleWishlist({
        contract: undoWishlistData?.contract,
        tokenId: undoWishlistData?.tokenId,
        collectionSlug: undoWishlistData?.collectionSlug,
        chain: undoWishlistData.chain,
    });

    const [storedRemoveWishListToastSlug] = useState(removeWishListToastSlug);

    useEffect(() => {
        let timeoutId;
        if (removeWishListToastSlug) {
            timeoutId = setTimeout(() => {
                dispatch(wishlistActions.setRemoveWishListToastSlug(null));
            }, 4000);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            dispatch(wishlistActions.setRemoveWishListToastSlug(null));
        };
    }, [dispatch, removeWishListToastSlug]);

    const handleUndo = () => {
        if (undoWishlistData?.wishlistSlug === storedRemoveWishListToastSlug) {
            handleAddFlow();
        }
    };

    return (
        <WishlistComponent hideStar={hideStar} moveUp={Boolean(wishListToastSlug)}>
            {removeWishListToastName && storedRemoveWishListToastSlug ? (
                <ToastLink to={`/wishlists/${storedRemoveWishListToastSlug}`}>
                    Removed from {removeWishListToastName}
                </ToastLink>
            ) : (
                'Removed from wishlist'
            )}
            {undoWishlistData && <Button onClick={handleUndo}>Undo</Button>}
        </WishlistComponent>
    );
};

const WishlistToastDelete = ({ label }: Props) => {
    const dispatch = useAppDispatch();
    const { showCreateWishListToast, showDeleteWishListToast } = useAppSelector((state) => state.wishlist);

    useEffect(() => {
        let timeoutId;
        if (showDeleteWishListToast) {
            timeoutId = setTimeout(() => {
                dispatch(wishlistActions.setShowDeleteWishListToast(false));
            }, 3500);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            dispatch(wishlistActions.setShowDeleteWishListToast(false));
        };
    }, [dispatch, showDeleteWishListToast]);

    return <WishlistComponent label={label} moveUp={Boolean(showCreateWishListToast)} />;
};

const WishlistToastUnfollow = ({ label }: Props) => {
    const dispatch = useAppDispatch();
    const { showUnfollowWishListToast } = useAppSelector((state) => state.wishlist);

    useEffect(() => {
        let timeoutId;
        if (showUnfollowWishListToast) {
            timeoutId = setTimeout(() => {
                dispatch(wishlistActions.setShowUnfollowWishListToast(false));
            }, 3500);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            dispatch(wishlistActions.setShowUnfollowWishListToast(false));
        };
    }, [dispatch, showUnfollowWishListToast]);

    return <WishlistComponent label={label} />;
};

const WishlistToastCreate = ({ label }: Props) => {
    const dispatch = useAppDispatch();
    const { showCreateWishListToast } = useAppSelector((state) => state.wishlist);

    useEffect(() => {
        let timeoutId;
        if (showCreateWishListToast) {
            timeoutId = setTimeout(() => {
                dispatch(wishlistActions.setShowCreateWishListToast(false));
            }, 3500);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            dispatch(wishlistActions.setShowCreateWishListToast(false));
        };
    }, [dispatch, showCreateWishListToast]);

    return <WishlistComponent label={label} />;
};

export const WishlistToast = () => {
    const {
        wishListToastSlug,
        removeWishListToastSlug,
        showUnfollowWishListToast,
        showDeleteWishListToast,
        showCreateWishListToast,
        deleteWishlistName,
    } = useAppSelector((state) => state.wishlist);

    return (
        <>
            <AnimatePresence>
                {showCreateWishListToast && <WishlistToastCreate label="Created new wishlist" />}
            </AnimatePresence>
            <AnimatePresence>
                {showUnfollowWishListToast && <WishlistToastUnfollow label="Unfollowed wishlist" />}
            </AnimatePresence>
            <AnimatePresence>
                {showDeleteWishListToast && <WishlistToastDelete label={`Deleted ${deleteWishlistName}`} />}
            </AnimatePresence>
            <AnimatePresence>
                {removeWishListToastSlug && <WishlistToastRemove hideStar label="Removed from wishlist" />}
            </AnimatePresence>
            <AnimatePresence>{wishListToastSlug && <WishlistToastAdded label="Added to wishlist" />}</AnimatePresence>
        </>
    );
};
